import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { motion, AnimatePresence } from "framer-motion";

// Components
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Stats from "./components/Stats";
import HowItWorks from "./components/HowItWorks";
import ChargersShowcase from "./components/ChargersShowcase";
import StationShowcase from "./components/StationShowcase";
import StationFinder from "./components/StationFinder";
import Coverage from "./components/Coverage";
import Pricing from "./components/Pricing";
import Calculator from "./components/Calculator";
import ProfitCalculator from "./components/ProfitCalculator";
import GovernmentPolicies from "./components/GovernmentPolicies";
import Franchise from "./components/Franchise";
import AppDownload from "./components/AppDownload";
import FAQ from "./components/FAQ";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import HotlineBanner from "./components/HotlineBanner";
import AdminDashboard from "./components/AdminDashboard";
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Initialize Lenis smooth scroll
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: "vertical",
      gestureDirection: "vertical",
      smooth: true,
      smoothTouch: false,
      touchMultiplier: 2,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => {
      clearTimeout(timer);
      lenis.destroy();
    };
  }, []);

  return (
    <AnimatePresence>
      {isLoading ? (
        <motion.div
          key="loader"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-background"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"
            />
            <h1 className="font-heading text-2xl text-primary">MILE PLUS</h1>
            <p className="text-foreground-muted mt-2">Powering Maharashtra</p>
          </motion.div>
        </motion.div>
      ) : (
        <motion.div
          key="content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="relative"
        >
          <HotlineBanner />
          <Navbar />
          <main className="pt-10">
            <Hero />
            <Stats />
            <HowItWorks />
            <ChargersShowcase />
            <StationShowcase />
            <StationFinder />
            <Coverage />
            <Pricing />
            <Calculator />
            <ProfitCalculator />
            <GovernmentPolicies />
            <Franchise />
            <AppDownload />
            <FAQ />
            <Contact />
          </main>
          <Footer />
          <WhatsAppButton />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const AdminPage = () => {
  const navigate = useNavigate();
  return <AdminDashboard onBack={() => navigate("/")} />;
};

function App() {
  return (
    <div className="App bg-background min-h-screen">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
